import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import canonicalService from '../services/canonicalService';
import canonicalgroupService from '../services/canonicalgroupService';
import './CanonicalForm.css';

function CanonicalForm({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [canonicalgroups, setCanonicalGroups] = useState([]);
  const [canonicalgroupsCurrentPage, setCanonicalGroupsCurrentPage] = useState(1);
  const canonicalgroupsPerPage = 20;
  const [canonicalgroupsSearchTerm, setCanonicalGroupsSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: ''
  });

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  useEffect(() => {
    if ((isEditMode || isViewMode) && id) {
      fetchCanonical();
      fetchCanonicalGroups();
    }
  }, [id, isEditMode, isViewMode]);

  const fetchCanonical = async () => {
    try {
      setLoading(true);
      const response = await canonicalService.getCanonicalById(id);
      const canonicalData = response.data;
      setFormData(canonicalData);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch canonical');
    } finally {
      setLoading(false);
    }
  };

  const fetchCanonicalGroups = async () => {
    try {
      const response = await canonicalgroupService.getAllCanonicalGroups({ canonicalid: id });
      setCanonicalGroups(response.data.canonicalgroups || []);
    } catch (err) {
      console.error('Failed to fetch canonical groups:', err);
    }
  };

  const handleDeleteCanonicalGroup = async (canonicalgroupId) => {
    if (!window.confirm('Are you sure you want to delete this canonical group?')) {
      return;
    }

    try {
      await canonicalgroupService.deleteCanonicalGroup(canonicalgroupId);
      // Refresh the canonical groups list
      fetchCanonicalGroups();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete canonical group');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate required fields
    if (!formData.name) {
      setError('Name is required');
      setLoading(false);
      return;
    }

    try {
      console.log('Submitting canonical data:', formData);

      if (isCreateMode) {
        const response = await canonicalService.createCanonical(formData);
        console.log('Canonical created:', response);
        // Navigate back to canonical list after create
        navigate('/admin/canonical');
      } else if (isEditMode) {
        const response = await canonicalService.updateCanonical(id, formData);
        console.log('Canonical updated:', response);
        // Navigate to canonical view screen after update
        navigate(`/admin/canonical/${id}`);
      }
    } catch (err) {
      console.error('Error saving canonical:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save canonical');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/canonical');
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this canonical?')) {
      return;
    }

    try {
      setLoading(true);
      await canonicalService.deleteCanonical(id);
      navigate('/admin/canonical');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete canonical');
      setLoading(false);
    }
  };

  if (loading && (isEditMode || isViewMode)) {
    return <div className="canonical-form-container">Loading canonical...</div>;
  }

  return (
    <div className="canonical-form-container">
      {/* Breadcrumb */}
      {(isViewMode || isEditMode) && formData.name && (
        <div className="breadcrumb">
          <Link to="/admin/canonical" className="breadcrumb-link">
            Canonicals
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{formData.name}</span>
        </div>
      )}

      <h2>
        {isCreateMode && 'Create New Canonical'}
        {isEditMode && 'Edit Canonical'}
        {isViewMode && 'View Canonical'}
      </h2>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <h3>Basic Information</h3>

          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isViewMode}
              required
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {isViewMode ? (
            <>
              <button type="button" onClick={() => navigate(`/admin/canonical/${id}/edit`)} title="Edit Canonical">
                ✏️ Edit
              </button>
              <button type="button" onClick={handleDelete} className="btn-danger" title="Delete Canonical">
                🗑️ Delete
              </button>
              <button type="button" onClick={handleCancel} title="Back to List">
                ← Back to List
              </button>
            </>
          ) : (
            <>
              <button type="submit" disabled={loading}>
                {loading ? 'Saving...' : isCreateMode ? 'Create Canonical' : 'Update Canonical'}
              </button>
              <button type="button" onClick={handleCancel} disabled={loading} title="Cancel">
                ← Cancel
              </button>
            </>
          )}
        </div>
      </form>

      {/* Canonical Groups Section - Only show in view/edit mode */}
      {(isViewMode || isEditMode) && id && (
        <div className="canonicalgroups-section">
          <div className="canonicalgroups-header">
            <h3>Canonical Groups</h3>
            <Link to={`/admin/canonical/${id}/canonicalgroup/new`} className="btn-add-canonicalgroup" title="Add Canonical Group">
              ➕ Add Canonical Group
            </Link>
          </div>

          {/* Search Field */}
          <div className="search-container">
            <input
              type="text"
              placeholder="Search canonical groups..."
              value={canonicalgroupsSearchTerm}
              onChange={(e) => {
                setCanonicalGroupsSearchTerm(e.target.value);
                setCanonicalGroupsCurrentPage(1); // Reset to first page on search
              }}
              className="search-input"
            />
          </div>

          {(() => {
            // Filter canonical groups based on search term
            const filteredCanonicalGroups = canonicalgroups.filter((canonicalgroup) =>
              canonicalgroup.name.toLowerCase().includes(canonicalgroupsSearchTerm.toLowerCase())
            );

            if (filteredCanonicalGroups.length === 0) {
              return <p className="no-canonicalgroups">No canonical groups found.</p>;
            }

            return (
              <>
                <div className="canonicalgroups-list">
                  {filteredCanonicalGroups
                    .slice(
                      (canonicalgroupsCurrentPage - 1) * canonicalgroupsPerPage,
                      canonicalgroupsCurrentPage * canonicalgroupsPerPage
                    )
                    .map((canonicalgroup) => (
                    <div
                      key={canonicalgroup.id}
                      className="canonicalgroup-card clickable-card"
                      onClick={() => navigate(`/admin/canonical/${id}/canonicalgroup/${canonicalgroup.id}`)}
                      title="Click to view"
                    >
                      <div className="canonicalgroup-info">
                        <div className="canonicalgroup-name">{canonicalgroup.name}</div>
                      </div>
                      <div className="canonicalgroup-actions">
                        <Link
                          to={`/admin/canonical/${id}/canonicalgroup/${canonicalgroup.id}/edit`}
                          className="btn-edit"
                          onClick={(e) => e.stopPropagation()}
                          title="Edit"
                        >
                          ✏️
                        </Link>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteCanonicalGroup(canonicalgroup.id);
                          }}
                          className="btn-delete"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    ))}
                </div>

                {/* Pagination controls */}
                {filteredCanonicalGroups.length > canonicalgroupsPerPage && (
                  <div className="pagination">
                    <button
                      onClick={() => setCanonicalGroupsCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={canonicalgroupsCurrentPage === 1}
                    >
                      Previous
                    </button>
                    <span>
                      Page {canonicalgroupsCurrentPage} of {Math.ceil(filteredCanonicalGroups.length / canonicalgroupsPerPage)}
                    </span>
                    <button
                      onClick={() =>
                        setCanonicalGroupsCurrentPage((prev) =>
                          Math.min(Math.ceil(filteredCanonicalGroups.length / canonicalgroupsPerPage), prev + 1)
                        )
                      }
                      disabled={canonicalgroupsCurrentPage >= Math.ceil(filteredCanonicalGroups.length / canonicalgroupsPerPage)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default CanonicalForm;
